import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DEFAULT_DOWNLOAD_FILE_NAME, } from '@qts-sdp-ui/shared-data';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { FormValidatorsService } from './form-validators.service';
import * as i0 from "@angular/core";
export class HelperService {
    constructor() {
        this._keepFilterOpen = new BehaviorSubject(false);
    }
    formatDateRange(startDate, endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startMonth = start.getMonth() + 1;
        const startDay = start.getDate();
        const endMonth = end.getMonth() + 1;
        const endDay = end.getDate();
        const endYear = end.getFullYear();
        return `${startMonth}-${startDay} - ${endMonth}-${endDay}-${endYear}`;
    }
    formatTimeRange(startTime, endTime) {
        const formattedStartTime = this.convertTo12Hour(startTime);
        const formattedEndTime = this.convertTo12Hour(endTime);
        return `${formattedStartTime} - ${formattedEndTime}`;
    }
    convertDateFormatForRequest(inputDate) {
        const date = new Date(inputDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const returnMonth = month < 10 ? '0' + month : month;
        const returnDay = day < 10 ? '0' + day : day;
        return `${year}-${returnMonth}-${returnDay}`;
    }
    convertTo12Hour(time) {
        // eslint-disable-next-line prefer-const
        let [hours, minutes] = time.split(':').map((num) => parseInt(num, 10));
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes < 10 ? '0' + minutes : minutes}${ampm}`;
    }
    substituteUrlParams(template, params, options) {
        if (!template || !params) {
            return template;
        }
        // eslint-disable-next-line no-prototype-builtins
        const defaultValueIsPresent = options?.hasOwnProperty('default');
        const defaultValue = defaultValueIsPresent ? options.default : undefined;
        const recursive = options?.recursive;
        const regex = /\$\{([^}]+)\}/g;
        return template.replace(regex, (match, varName) => {
            const varValue = varName in params
                ? params[varName]
                : defaultValueIsPresent
                    ? defaultValue
                    : match;
            return recursive
                ? this.substituteUrlParams(varValue, params, options)
                : varValue;
        });
    }
    getBinaryPayload(files) {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('attachments', file);
        });
        return formData;
    }
    getDownloadFileName(fileName = DEFAULT_DOWNLOAD_FILE_NAME) {
        return `${fileName}_${(new Date().getUTCMonth() + 1)
            .toString()
            .padStart(2, '0')}_${new Date()
            .getUTCDate()
            .toString()
            .padStart(2, '0')}_${new Date().getUTCFullYear()}_${new Date()
            .getUTCHours()
            .toString()
            .padStart(2, '0')}-${new Date()
            .getUTCMinutes()
            .toString()
            .padStart(2, '0')}`;
    }
    getUrlEncodedString(urlParams) {
        const params = new URLSearchParams(urlParams);
        return params.toString();
    }
    convertToFirstCap(text) {
        // Split the text into words
        const words = text.toLowerCase().split(' ');
        // Capitalize the first letter of each word
        const convertedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
        // Join the words back into a sentence
        return convertedWords.join(' ');
    }
    scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    compareObjects(obj1, obj2) {
        if (_.isEqual(obj1, obj2)) {
            return null; // Objects are equal
        }
        if (_.isObject(obj1) && _.isObject(obj2)) {
            const keys = _.union(_.keys(obj1), _.keys(obj2));
            return keys.reduce((differences, key) => {
                const value1 = obj1[key];
                const value2 = obj2[key];
                if (!_.isEqual(value1, value2)) {
                    if (_.isObject(value1) && _.isObject(value2)) {
                        const nestedDifferences = this.compareObjects(value1, value2);
                        if (nestedDifferences) {
                            differences[key] = { oldValue: value1, newValue: value2 };
                        }
                    }
                    else {
                        differences[key] = { oldValue: value1, newValue: value2 };
                    }
                }
                return differences;
            }, {});
        }
        return { oldValue: obj1, newValue: obj2 }; // Adjust the type here
    }
    filterByInterfaceKeys(differingValues, keysToInclude) {
        if (differingValues === null) {
            return null;
        }
        const filteredValues = {};
        for (const key of keysToInclude) {
            if (key in differingValues) {
                filteredValues[key] = differingValues[key];
            }
        }
        return filteredValues;
    }
    getInitials(fullName) {
        if (!fullName)
            return 'NA';
        const allNames = fullName.trim().split(' ');
        const initials = allNames.reduce((acc, curr, index) => {
            if (index === 0 || index === allNames.length - 1) {
                acc = `${acc}${curr.charAt(0).toUpperCase()}`;
            }
            return acc;
        }, '');
        return initials;
    }
    formatePhone(input) {
        input = input.trim();
        if (input)
            return input.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') ?? input;
        return input;
    }
    getDecoratedQueryParams(queryParameters) {
        if (!queryParameters.length)
            return '';
        return queryParameters
            .filter((item) => item.paramValue !== '' && item.paramKey !== '')
            .map((param) => `${param.paramKey}=${param.paramValue}`)
            .join('&');
    }
    getCompareResult(value1, value2) {
        let result = 0;
        if (value1 == null && value2 != null) {
            result = -1;
        }
        else if (value1 != null && value2 == null) {
            result = 1;
        }
        else if (value1 == null && value2 == null) {
            result = 0;
        }
        else if (typeof value1 === 'string' && typeof value2 === 'string') {
            result = value1.localeCompare(value2);
        }
        else {
            result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
        }
        return result;
    }
    set keepFilterOpen(value) {
        this._keepFilterOpen.next(value);
    }
    get keepFilterOpen() {
        return this._keepFilterOpen.getValue();
    }
    keepFilter(element) {
        const isSVGAnimatedString = (cls) => {
            return cls instanceof SVGAnimatedString;
        };
        const lastChildClassName = element.lastChild?.className;
        const parentElementClassName = element.parentElement?.className;
        if ((isSVGAnimatedString(lastChildClassName) && lastChildClassName.baseVal) ||
            (isSVGAnimatedString(parentElementClassName) &&
                parentElementClassName.baseVal)) {
            return (lastChildClassName?.baseVal?.includes('p-multiselect-token-icon') ||
                parentElementClassName?.baseVal?.includes('p-datepicker-next-icon') ||
                parentElementClassName?.baseVal?.includes('p-datepicker-prev-icon'));
        }
        const className = element?.className;
        if (isSVGAnimatedString(className)) {
            return (className.baseVal.includes('p-toast-icon-close-icon') ||
                className.baseVal.includes('p-dropdown-trigger-icon') ||
                className.baseVal.includes('p-datepicker-next-icon') ||
                className.baseVal.includes('p-datepicker-prev-icon'));
        }
        const checkClasses = (classList, className) => {
            return classList.some((cls) => className?.includes(cls));
        };
        const classChecks = [
            {
                className: element.className,
                classes: [
                    'p-component-overlay',
                    'qts-single-input',
                    'search',
                    'qts-table',
                    'qts-icon',
                    'qts-button',
                    'qts-toast',
                    'qts-badge',
                    'qts-modal',
                    'p-datepicker',
                    'p-yearpicker',
                    'p-monthpicker',
                    'p-dropdown-trigger',
                    'p-dropdown-item',
                    'p-datepicker-prev',
                    'p-datepicker-next',
                ],
            },
            {
                className: element.parentElement?.className,
                classes: [
                    'qts-applied-filter-tag',
                    'qts-button',
                    'p-component-overlay',
                    'qts-badge',
                ],
            },
        ];
        const clickedTableContainer = element.offsetParent
            ? element.offsetParent.className?.includes('qts-table') ?? false
            : false;
        const clickedModalContainer = element.firstElementChild &&
            typeof element.firstElementChild.className === 'string'
            ? element.firstElementChild.className?.includes('qts-modal') ?? false
            : false;
        for (const check of classChecks) {
            if (checkClasses(check.classes, check.className)) {
                return true;
            }
        }
        return clickedTableContainer || clickedModalContainer;
    }
    // Function to compare current form values with original values
    areEqual(obj1, obj2) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }
    getDefaultTimepickerControl(options = {}) {
        const { value = null, validators = [], isRequired = true } = options;
        return new FormControl(value, {
            validators: [
                FormValidatorsService.timeFormateValidator(),
                ...(isRequired ? [Validators.required] : []),
                ...validators,
            ],
        });
    }
    deepCopy(obj) {
        // Check if the value is an object or an array
        if (obj && typeof obj === 'object') {
            // Create an array or object to hold the values
            const copy = Array.isArray(obj) ? [] : {};
            for (const key in obj) {
                // Use Object.prototype.hasOwnProperty.call to avoid issues
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    // Recursively (deep) copy for nested objects, including arrays
                    copy[key] = this.deepCopy(obj[key]);
                }
            }
            return copy;
        }
        return obj;
    }
    static { this.ɵfac = function HelperService_Factory(t) { return new (t || HelperService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HelperService, factory: HelperService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HelperService, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], null, null); })();
